"use client";

/** Decode a base64url VAPID key into the byte array the Push API expects. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Return a live push subscription bound to the CURRENT VAPID key.
 *
 * iOS Safari (and others) keep handing back a subscription object even after the
 * push service has invalidated it, and `subscribe()` throws if an existing
 * subscription was created with a different applicationServerKey. So we compare
 * the stored key against the current one and, if it differs (or is missing),
 * unsubscribe the stale one before creating a fresh subscription. This makes
 * both first-time opt-in and recovery after a VAPID rotation reliable.
 */
export async function ensurePushSubscription(
  vapidPublicKey: string
): Promise<PushSubscription | null> {
  if (
    typeof window === "undefined" ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !vapidPublicKey
  ) {
    return null;
  }

  const reg = await navigator.serviceWorker.ready;
  const currentKey = urlBase64ToUint8Array(vapidPublicKey);
  const existing = await reg.pushManager.getSubscription();

  if (existing) {
    const existingKey = existing.options?.applicationServerKey;
    const matches =
      existingKey != null &&
      bytesEqual(new Uint8Array(existingKey as ArrayBuffer), currentKey);
    if (matches) return existing;
    // Stale or created with a different key — drop it before re-subscribing.
    await existing.unsubscribe().catch(() => {});
  }

  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    // DOM types want an ArrayBuffer-backed view; the runtime Uint8Array is valid.
    applicationServerKey: currentKey as BufferSource,
  });
}

/** POST the subscription to the server. `setEnabled` flips the user preference on explicit opt-in. */
export async function syncPushSubscription(
  sub: PushSubscription,
  setEnabled: boolean
): Promise<boolean> {
  const json = sub.toJSON();
  const res = await fetch("/api/push/subscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys, setEnabled }),
  }).catch(() => null);
  return Boolean(res && res.ok);
}
